'use client';

import { fetchApi } from '@/requests/api';
import { BASE_DASHBOARD_URL } from '@/requests/constants';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import queryString from 'query-string';
import { z } from 'zod/v4';
import { PRESETS, PresetSchema, type Preset } from '../schema';
import { stringToJSONSchema } from '../schema/utils';
import { useSession } from '@/providers/session-provider';
import { reportError } from '@/datadog/report-error';

export const PRESET_QUERY_KEY = 'presets';

const PresetResponseSchema = z.object({
  next_pagination_key: z
    .object({
      key: z.object({ S: z.string() }),
      owner: z.object({ S: PresetSchema.shape.owner }),
    })
    .nullable(),
  presets: z.array(
    z.object({
      // We want to make sure that if we change something in the data field,
      // e.g. adding a new field. We don't want the presets to be stopped at
      // fetch level. Use looseObject to not strip but allow us to parse that
      // version is there.
      ...PresetSchema.shape,
      // The backend stores presets of every build that shares it, so a
      // context this build does not know yet must not fail the whole fetch.
      // Unknown contexts are dropped in `fetchPresets` instead.
      context: z.string(),
      data: z.looseObject({
        version: PresetSchema.shape.data.shape.version,
        presetData: stringToJSONSchema,
      }),
    })
  ),
});

type FetchedPreset = z.infer<typeof PresetResponseSchema>['presets'][number];
type KnownPreset = Omit<FetchedPreset, 'context'> & {
  context: Preset['context'];
};

export type PresetResponse = Omit<
  z.infer<typeof PresetResponseSchema>,
  'presets'
> & { presets: KnownPreset[] };

function isKnownPreset(preset: FetchedPreset): preset is KnownPreset {
  return preset.context in PRESETS;
}

function dropUnknownPresets(presets: FetchedPreset[]): KnownPreset[] {
  const unknownContexts = new Set(
    presets.filter((preset) => !isKnownPreset(preset)).map((p) => p.context)
  );
  if (unknownContexts.size > 0) {
    reportError('presets', new Error('Dropped presets with unknown context'), {
      resource: { url: '/presets' },
      presets: { unknownContexts: Array.from(unknownContexts) },
    });
  }
  return presets.filter(isKnownPreset);
}

async function fetchPresets(
  pagination_key: z.infer<typeof PresetResponseSchema>['next_pagination_key'],
  owner: Preset['owner']
): Promise<PresetResponse['presets']> {
  const params = queryString.stringify({
    owner: owner,
    page_limit: 50,
    pagination_key: pagination_key ? JSON.stringify(pagination_key) : null,
  });

  const url = `${BASE_DASHBOARD_URL}/presets?${params}`;

  const response = await fetchApi({
    url,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch preset');
  }

  const resBody = await response.json();
  const parseRes = PresetResponseSchema.safeParse(resBody);

  if (parseRes.success) {
    const presets = dropUnknownPresets(parseRes.data.presets);
    if (parseRes.data.next_pagination_key) {
      const nested = await fetchPresets(
        parseRes.data.next_pagination_key,
        owner
      );
      return [...presets, ...nested];
    }
    return presets;
  } else {
    reportError('presets', new Error('Failed to parse preset response'), {
      resource: { url: '/presets' },
      validation: {
        issues: parseRes.error.issues.map(({ code, path }) => ({ code, path })),
      },
    });
    throw parseRes.error;
  }
}

export function useGetPreset(
  publicity: 'private' | 'public',
  options?: Omit<
    UseQueryOptions<Array<PresetResponse['presets'][number]>>,
    'queryKey'
  >
) {
  const {
    data: {
      session: { customerId, token },
    },
  } = useSession();
  const {
    data: { user },
  } = useSession();

  const owner =
    publicity === 'private'
      ? (`private:${customerId}:${user?.id}` as const)
      : (`public:${customerId}` as const);

  return useQuery({
    ...options,
    queryKey: [PRESET_QUERY_KEY, owner],
    queryFn: async () => fetchPresets(null, owner),
    enabled: !!token && Boolean(user) && (options ? options.enabled : true),
    staleTime: 60 * 5 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
